import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Inventory2Outlined as MachineIcon, SearchOff as NoResultsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors, spacing } from '../../themes/typography';
import CommandCenterTopBar from './components/CommandCenterTopBar';
import MachineStrip from './components/MachineStrip';
import EnhancedPlanogram from './components/EnhancedPlanogram';
import ExperimentModePanel from './components/ExperimentModePanel';
import SlotDetailDrawer from './components/SlotDetailDrawer';
import BackToAlertsLink from './components/BackToAlertsLink';
import useSlotSelection from './hooks/useSlotSelection';
import { fetchMachinesData, fetchMachineFilterOptions, lookupMachineByNumber } from '../../data/machinesApi';
import { useAuth } from '../../context/AuthContext';
import { POSAnalysisTab } from './POSAnalysisTab';

// A lane with an experiment under way (proposed, accepted, scheduled or running) can't be given another change
const hasOpenExperiment = (slot) => Boolean(slot?.experiment) && !['rejected', 'finished'].includes(slot.experiment.status);
// The orchestrator's proposal, waiting to be accepted or rejected ("Sugerencia IA")
const hasSuggestion = (slot) => slot?.experiment?.status === 'manual_review';

export const OverviewTab = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // An alert deep-link carries the internal machine id plus its group/location, so the
  // planogram can load that machine straight away without a lookup.
  const alertLink = location.state?.machineId && location.state?.filters ? location.state : null;
  // The alert page is the previous history entry, so Back keeps its filters, sort and page
  const handleBackToAlerts = React.useCallback(() => navigate(-1), [navigate]);

  // Initialize state with filters from the alert deep-link or sessionStorage
  const [state, setState] = React.useState(() => {
    const savedFilters = sessionStorage.getItem('analysis-page-filters');
    const filters = alertLink
      ? { group: alertLink.filters.group || '', location: alertLink.filters.location || '' }
      : savedFilters ? JSON.parse(savedFilters) : { group: '', location: '' };

    return {
      filters,
      filterOptions: { groups: [] },
      availableLocations: [],
      availableMachines: [],
      machinesData: [],
      selectedMachineId: null
    };
  });

  // Initialize experimentStates
  const [experimentStates, setExperimentStates] = React.useState({});

  // Period
  const [period, setPeriod] = React.useState(() => {
    const saved = sessionStorage.getItem('machines-period');
    return saved ? parseInt(saved) : 14;
  });

  // Destructure state for easier access
  const { filters, filterOptions, availableLocations, machinesData, selectedMachineId } = state;

  // Derive selectedMachine from machinesData + selectedMachineId
  const selectedMachine = React.useMemo(() => {
    if (!selectedMachineId || !machinesData.length) return null;
    return machinesData.find(m => m.machineId === selectedMachineId) || null;
  }, [selectedMachineId, machinesData]);

  // Loading states (not cached)
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  // Slot selection
  const { selectedSlots, toggleSlot, clearSelection, getSelectedSlotData } = useSlotSelection();

  // Experiment mode: a lane click queues it (the selection) and, once picking, makes its product the one coming in
  const [experimentMode, setExperimentMode] = React.useState(false);
  const [picking, setPicking] = React.useState(false);
  const [incoming, setIncoming] = React.useState(null); // { option, laneKey }: what comes in and the lane clicked for it
  // The lane under the pointer and its cell, while it is there
  const [pointer, setPointer] = React.useState(null);
  // The lane with an experiment under way just clicked: its experiment shows over it while the pointer stays there
  const [busyKey, setBusyKey] = React.useState(null);
  // The suggestion lane clicked: its proposal fills the panel
  const [suggestionKey, setSuggestionKey] = React.useState(null);
  const handleSlotHover = React.useCallback((slot, el) => setPointer({ slot, el }), []);
  const handleSlotLeave = React.useCallback(() => { setPointer(null); setBusyKey(null); }, []);
  const [recommendations, setRecommendations] = React.useState([]);

  // Slot detail drawer state — store slot key, derive live data from enriched machine
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerSlotKey, setDrawerSlotKey] = React.useState(null);

  // Ref to hold a pending machine ID to auto-select after machines load
  const pendingMachineIdRef = React.useRef(alertLink ? String(alertLink.machineId) : null);
  // Slot an alert deep-link asked for: { machineId, productId, selectionCodes }
  const pendingSlotRef = React.useRef(alertLink?.productId
    ? {
        machineId: String(alertLink.machineId),
        productId: Number(alertLink.productId),
        selectionCodes: (alertLink.selectionCodes || []).map(String)
      }
    : null);

  // Clear slot selection when machine changes
  React.useEffect(() => {
    clearSelection();
    setDrawerOpen(false);
    setDrawerSlotKey(null);
  }, [selectedMachineId, clearSelection]);

  // Stable callback for experiment state changes
  const handleExperimentStateChange = React.useCallback((newStatesOrUpdater) => {
    setExperimentStates(prev => {
      const newState = typeof newStatesOrUpdater === 'function' ? newStatesOrUpdater(prev) : newStatesOrUpdater;
      return newState;
    });
  }, []);

  // Period change handler
  const handlePeriodChange = React.useCallback((newPeriod) => {
    setPeriod(newPeriod);
    sessionStorage.setItem('machines-period', String(newPeriod));
  }, []);

  // Define loadMachineData first so it can be used in refreshDataAfterMutation
  // isRefresh=true avoids the skeleton flash (keeps stale data visible while loading)
  const loadMachineData = React.useCallback(async (isRefresh = false) => {
    if (!filters.group) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const filteredMachines = await fetchMachinesData({ ...filters, period }, currentUser.customer_id);

      // Clear selected machine if it's no longer in the filtered results
      const newSelectedMachineId = selectedMachineId && !filteredMachines.find(m => m.machineId === selectedMachineId)
        ? null
        : selectedMachineId;

      setState(prev => ({
        ...prev,
        machinesData: filteredMachines,
        selectedMachineId: newSelectedMachineId
      }));

      // Clear optimistic experiment states — backend data now has full experiment info
      setExperimentStates({});

    } catch (error) {
      setState(prev => ({
        ...prev,
        machinesData: [],
        selectedMachineId: null
      }));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, period, currentUser.customer_id, selectedMachineId]);

  // Function to refresh data after any mutation (experiment accept/reject, etc.)
  // Does a silent background refresh without nulling the selected machine
  const refreshDataAfterMutation = React.useCallback(async () => {
    if (filters.group) {
      await loadMachineData(true); // isRefresh=true avoids skeleton flash
    }
  }, [filters.group, loadMachineData]);

  // Optimistic update: instantly show experiment badge on a slot after creation
  const addOptimisticExperiment = React.useCallback((machineId, row, col, experimentData) => {
    const machineKey = `${machineId}-${row}-${col}`;
    setExperimentStates(prev => ({
      ...prev,
      [machineKey]: {
        id: experimentData?.experiment?.id || Date.now(),
        status: experimentData?.experiment?.status || 'pending',
        experimentType: experimentData?.experiment?.experimentType || 'promo_precio_unitario',
      }
    }));
  }, []);

  // Listen for experiment state changes that require data refresh
  React.useEffect(() => {
    const handleExperimentMutation = () => {
      refreshDataAfterMutation();
    };

    window.addEventListener('experimentMutation', handleExperimentMutation);

    return () => {
      window.removeEventListener('experimentMutation', handleExperimentMutation);
    };
  }, [refreshDataAfterMutation]);

  // Save filters to sessionStorage whenever they change
  React.useEffect(() => {
    sessionStorage.setItem('analysis-page-filters', JSON.stringify(filters));
  }, [filters]);

  // Load filter options on component mount
  React.useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchMachineFilterOptions(currentUser.customer_id);
        setState(prev => ({
          ...prev,
          filterOptions: options
        }));

        // Check for filters passed from navigation state (from insights/diagnostics page)
        const passedFilters = location.state?.filters;
        const passedMachineStoreId = location.state?.machineStoreId;

        if (location.state?.machineId) {
          // Alert deep-link: machine and filters were applied at mount
        } else if (passedMachineStoreId && currentUser?.customer_id) {
          // Lookup machine by store_id and set filters + pending selection
          try {
            const machineInfo = await lookupMachineByNumber(currentUser.customer_id, passedMachineStoreId);
            if (machineInfo) {
              pendingMachineIdRef.current = String(machineInfo.id);
              setState(prev => ({
                ...prev,
                selectedMachineId: null,
                filters: {
                  ...prev.filters,
                  group: machineInfo.group || '',
                  location: machineInfo.location || ''
                }
              }));
            }
          } catch (err) {
            console.error('[Machines] lookupMachine from nav state failed:', err);
          }
        } else if (passedFilters) {
          setState(prev => ({
            ...prev,
            filters: {
              ...prev.filters,
              group: passedFilters.group || '',
              location: passedFilters.location || ''
            }
          }));
        }
      } catch (error) {
      }
    };

    if (currentUser?.customer_id) {
      loadFilterOptions();
    }
  }, [location.state, currentUser]);

  // Clear sidebar on mount and unmount
  React.useEffect(() => {
    window.currentPageSidebar = null;
    window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    return () => {
      window.currentPageSidebar = null;
      window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    };
  }, []);

  // Filter handlers
  const handleFilterChange = (filterType, value) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterType]: value
      }
    }));
  };

  // When group changes, update available locations
  React.useEffect(() => {
    if (!filterOptions.groups || filterOptions.groups.length === 0) {
      return;
    }

    const selectedGroup = filterOptions.groups.find(g => g.id === filters.group);
    const allLocations = [];

    selectedGroup?.clusters?.forEach(cluster => {
      if (cluster.locations) {
        allLocations.push(...cluster.locations);
      }
    });

    const currentLocationIsValid = allLocations.some(loc => loc.id === filters.location);

    setState(prev => ({
      ...prev,
      availableLocations: allLocations,
      filters: {
        ...prev.filters,
        location: currentLocationIsValid ? prev.filters.location : ''
      }
    }));
  }, [filters.group, filterOptions]);

  // Auto-search when filters change
  const prevGroupRef = React.useRef(filters.group);
  React.useEffect(() => {
    if (filters.group) {
      // Treat group change as a fresh load (show skeleton), same-group filter tweaks as refresh
      const groupChanged = prevGroupRef.current !== filters.group;
      prevGroupRef.current = filters.group;
      const isRefresh = !groupChanged && machinesData.length > 0;

      // Immediately show loading skeleton on group change (don't wait for debounce)
      if (groupChanged) {
        setLoading(true);
        setState(prev => ({ ...prev, machinesData: [], selectedMachineId: null }));
      }

      // The debounce batches group+location edits; the first load on mount has nothing to batch
      const isFirstLoad = !groupChanged && machinesData.length === 0;
      const timeoutId = setTimeout(() => {
        loadMachineData(isRefresh);
      }, isFirstLoad ? 0 : 300);

      return () => clearTimeout(timeoutId);
    } else {
      prevGroupRef.current = filters.group;
      setState(prev => ({
        ...prev,
        machinesData: [],
        selectedMachineId: null
      }));
    }
  }, [filters.group, filters.location, period]);

  // Auto-select the first machine when machines load (or pending machine from search/navigation)
  React.useEffect(() => {
    if (!loading && machinesData.length > 0 && !selectedMachineId) {
      if (pendingMachineIdRef.current) {
        const pending = String(pendingMachineIdRef.current);
        pendingMachineIdRef.current = null;
        const found = machinesData.find(m => String(m.machineId) === pending);
        if (!found) pendingSlotRef.current = null;
        setState(prev => ({ ...prev, selectedMachineId: found ? found.machineId : machinesData[0].machineId }));
      } else {
        setState(prev => ({ ...prev, selectedMachineId: machinesData[0].machineId }));
      }
    }
  }, [loading, machinesData, selectedMachineId]);

  // Handle machine selection from strip or top bar
  const handleSelectMachine = React.useCallback((machineId) => {
    setState(prev => ({ ...prev, selectedMachineId: machineId }));
  }, []);

  // Handle machine number search from CommandCenterTopBar
  const handleMachineSearch = React.useCallback(async (storeId, option) => {
    if (!currentUser?.customer_id) return;
    try {
      // If option has group/location (from dropdown), use directly — no API call needed
      const machineInfo = option?.group
        ? { id: option.id, group: option.group, location: option.location }
        : await lookupMachineByNumber(currentUser.customer_id, storeId);

      if (machineInfo) {
        const machineId = String(machineInfo.id);
        const filtersChanged = machineInfo.group !== filters.group || machineInfo.location !== filters.location;

        if (filtersChanged) {
          pendingMachineIdRef.current = machineId;
          setState(prev => ({
            ...prev,
            selectedMachineId: null,
            filters: {
              ...prev.filters,
              group: machineInfo.group || '',
              location: machineInfo.location || ''
            }
          }));
        } else {
          setState(prev => ({ ...prev, selectedMachineId: machineId }));
        }
      }
    } catch (err) {
      console.error('[Machines] Machine lookup failed:', err);
    }
  }, [currentUser, filters.group, filters.location]);

  // Helper function to enrich machine data with experiment states
  const enrichMachineData = React.useCallback((machineData) => {
    if (!machineData) return machineData;

    return {
      ...machineData,
      slots: machineData.slots.map(slot => {
        const productKey = `${slot.row}-${slot.column}`;
        const machineKey = `${machineData.machineId}-${productKey}`;
        const experimentState = experimentStates[machineKey];

        if (experimentState) {
          return {
            ...slot,
            problem: slot.problem,
            experiment: experimentState
          };
        }

        return slot;
      })
    };
  }, [experimentStates]);

  // Enriched selected machine
  const enrichedSelectedMachine = React.useMemo(() => {
    return selectedMachine ? enrichMachineData(selectedMachine) : null;
  }, [selectedMachine, enrichMachineData]);

  // Derive live drawer slot data from enriched machine
  const drawerSlotData = React.useMemo(() => {
    if (!drawerSlotKey || !enrichedSelectedMachine?.slots) return null;
    return enrichedSelectedMachine.slots.find(
      s => s.row === drawerSlotKey.row && s.column === drawerSlotKey.column
    ) || null;
  }, [drawerSlotKey, enrichedSelectedMachine]);

  // Experiment mode on: the sales heatmap and an empty queue; off: the products view
  const toggleExperimentMode = React.useCallback(() => {
    clearSelection();
    setExperimentMode(!experimentMode);
    setPicking(false);
    setIncoming(null);
    setPointer(null);
    setSuggestionKey(null);
  }, [experimentMode, clearSelection]);

  // Another machine, or the queue emptied: nothing to pick for
  React.useEffect(() => {
    setPicking(false);
    setIncoming(null);
    setPointer(null);
    setSuggestionKey(null);
  }, [selectedMachineId]);
  React.useEffect(() => {
    if (selectedSlots.size === 0) {
      setPicking(false);
      setIncoming(null);
    }
  }, [selectedSlots]);

  const handleExperimentsCreated = React.useCallback((createdSlots) => {
    // Optimistic update: show experiment badges instantly for all created lanes
    createdSlots.forEach((slot) => {
      addOptimisticExperiment(enrichedSelectedMachine.machineId, slot.row, slot.column, slot._experimentResponse);
    });
    refreshDataAfterMutation();
    clearSelection();
  }, [enrichedSelectedMachine, addOptimisticExperiment, refreshDataAfterMutation, clearSelection]);

  const handleRemoveQueued = React.useCallback((slots) => slots.forEach((slot) => toggleSlot(slot)), [toggleSlot]);

  // Inspect: click on slot opens drawer (does NOT affect selection)
  const handleSlotInspect = React.useCallback((slot) => {
    if (slot && slot.name) {
      setDrawerSlotKey({ row: slot.row, column: slot.column });
      setDrawerOpen(true);
    }
  }, []);

  // A lane click: its drawer, or — in experiment mode — its suggestion in the panel (clicked again, closed), into or
  // out of the queue, or (picking) the product coming in; a queued lane can't come in, and the lane clicked again is no
  // longer chosen
  const handleSlotClick = React.useCallback((slot) => {
    if (!experimentMode) {
      handleSlotInspect(slot);
      return;
    }
    if (!slot?.name) return;
    const key = `${slot.row}-${slot.column}`;
    if (!picking) {
      if (hasSuggestion(slot)) {
        setSuggestionKey((prev) => (prev === key ? null : key));
        return;
      }
      setSuggestionKey(null);
      if (hasOpenExperiment(slot)) setBusyKey(key);
      else toggleSlot(slot);
      return;
    }
    if (selectedSlots.has(key)) return;
    const productId = Number(slot.d_product_id);
    setIncoming((prev) => (prev?.laneKey === key ? null : {
      option: recommendations.find((o) => o.id === productId) ?? { id: productId, name: slot.name, group: 'all' },
      laneKey: key,
    }));
  }, [experimentMode, picking, selectedSlots, recommendations, toggleSlot, handleSlotInspect]);

  // The queue as lanes, the same array until it changes (the panel keys its suggestions on it)
  const queuedLanes = React.useMemo(
    () => (enrichedSelectedMachine ? getSelectedSlotData(enrichedSelectedMachine.slots) : []),
    [enrichedSelectedMachine, getSelectedSlotData, selectedSlots] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // While picking, the product of the pickable lane under the pointer, shown for a moment as the one coming in
  const previewIncoming = React.useMemo(() => {
    const slot = pointer?.slot;
    if (!picking || !slot?.name || selectedSlots.has(`${slot.row}-${slot.column}`)) return null;
    const productId = Number(slot.d_product_id);
    return recommendations.find((o) => o.id === productId) ?? { id: productId, name: slot.name, group: 'all' };
  }, [picking, pointer, selectedSlots, recommendations]);

  // The suggestion open in the panel, live from the machine's data, with every lane its proposal changes
  const suggestion = React.useMemo(() => {
    const slot = suggestionKey && enrichedSelectedMachine?.slots.find((s) => `${s.row}-${s.column}` === suggestionKey);
    if (!hasSuggestion(slot)) return null;
    const lanes = enrichedSelectedMachine.slots.filter((s) => s.experiment?.id === slot.experiment.id);
    return { experiment: slot.experiment, lanes };
  }, [suggestionKey, enrichedSelectedMachine]);

  const handleSuggestionResolved = React.useCallback(() => {
    setSuggestionKey(null);
    refreshDataAfterMutation();
  }, [refreshDataAfterMutation]);

  // While picking: the queued lanes faded, the lane picked to come in, the recommended products starred, every
  // other lane with a product pickable. Otherwise, the lanes of the suggestion open in the panel
  const slotMarkers = React.useMemo(() => {
    if (!experimentMode || !enrichedSelectedMachine) return undefined;
    if (!picking) {
      return suggestion
        ? new Map(suggestion.lanes.map((s) => [`${s.row}-${s.column}`, 'suggestion']))
        : undefined;
    }
    const recommended = new Set(recommendations.map((o) => o.id));
    const marks = new Map();
    enrichedSelectedMachine.slots.forEach((slot) => {
      const key = `${slot.row}-${slot.column}`;
      if (selectedSlots.has(key)) marks.set(key, 'dimmed');
      else if (incoming?.laneKey === key) marks.set(key, 'picked');
      else if (recommended.has(Number(slot.d_product_id))) marks.set(key, 'recommended');
      else if (slot.name) marks.set(key, 'pickable');
    });
    return marks;
  }, [experimentMode, picking, enrichedSelectedMachine, selectedSlots, incoming, recommendations, suggestion]);

  // Open the drawer on the slot an alert deep-link points at, once its machine is shown
  React.useEffect(() => {
    const pending = pendingSlotRef.current;
    if (!pending || !enrichedSelectedMachine?.slots) return;
    if (String(enrichedSelectedMachine.machineId) !== pending.machineId) return;
    pendingSlotRef.current = null;

    const sameProduct = enrichedSelectedMachine.slots.filter(s => Number(s.d_product_id) === pending.productId);
    const slot = sameProduct.find(s => pending.selectionCodes.includes(String(s.selection_id))) || sameProduct[0];
    if (slot) handleSlotInspect(slot);
  }, [enrichedSelectedMachine, handleSlotInspect]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!machinesData.length) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'Escape') {
        if (drawerOpen) {
          setDrawerOpen(false);
          return;
        }
        // Experiment mode, one step back each time: the suggestion open, picking, then the queue, then the mode itself
        if (experimentMode) {
          if (suggestionKey) {
            setSuggestionKey(null);
          } else if (picking) {
            setPicking(false);
            setIncoming(null);
          } else if (selectedSlots.size > 0) {
            clearSelection();
          } else {
            toggleExperimentMode();
          }
          return;
        }
        clearSelection();
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const currentIndex = machinesData.findIndex(m => m.machineId === selectedMachineId);
        let nextIndex;
        if (e.key === 'ArrowRight') {
          nextIndex = currentIndex < machinesData.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : machinesData.length - 1;
        }
        setState(prev => ({ ...prev, selectedMachineId: machinesData[nextIndex].machineId }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [machinesData, selectedMachineId, drawerOpen, experimentMode, suggestionKey, picking, selectedSlots, toggleExperimentMode, clearSelection]);

  return (
    <Box sx={{
      backgroundColor: colors.background.page,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {alertLink && (
        <Box sx={{ px: 2, pt: 1 }}>
          <BackToAlertsLink onClick={handleBackToAlerts} />
        </Box>
      )}

      {/* Sticky Top Bar */}
      <CommandCenterTopBar
        filters={filters}
        filterOptions={filterOptions}
        availableLocations={availableLocations}
        onFilterChange={handleFilterChange}
        machinesData={machinesData}
        selectedMachineId={selectedMachineId}
        onSelectMachine={handleSelectMachine}
        selectedMachine={enrichedSelectedMachine}
        loading={loading}
        experimentMode={experimentMode}
        onExperimentModeChange={toggleExperimentMode}
        period={period}
        onPeriodChange={handlePeriodChange}
        onMachineSearch={handleMachineSearch}
      />

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {/* Loading skeleton */}
        {loading && (
          <Box sx={{ py: 2 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' },
              gap: '6px',
              maxWidth: 900,
              mx: 'auto'
            }}>
              {Array.from({ length: 30 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '6px' }} />
              ))}
            </Box>
          </Box>
        )}

        {/* No machines found */}
        {!loading && machinesData.length === 0 && filters.group && (
          <Box sx={{
            textAlign: 'center',
            py: spacing.xxl,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            <NoResultsIcon sx={{ fontSize: 48, color: colors.text.muted, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '15px', fontWeight: 500, color: colors.text.secondary }}>
              {t('machines_noMachinesFound')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: colors.text.muted }}>
              {t('machines_tryAdjustingFilters')}
            </Typography>
          </Box>
        )}

        {/* No group selected */}
        {!loading && machinesData.length === 0 && !filters.group && (
          <Box sx={{
            textAlign: 'center',
            py: spacing.xxl,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            <MachineIcon sx={{ fontSize: 48, color: colors.text.muted, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '15px', fontWeight: 500, color: colors.text.secondary }}>
              {t('machines_selectGroupToStart')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: colors.text.muted }}>
              {t('machines_chooseGroupMessage')}
            </Typography>
          </Box>
        )}

        {/* Planogram (hero) */}
        {!loading && enrichedSelectedMachine && (
          <Box
            key={enrichedSelectedMachine.machineId}
            sx={{
              animation: 'fadeIn 200ms ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 }
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none'
              },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              opacity: refreshing ? 0.5 : 1,
              transition: 'opacity 200ms ease',
              pointerEvents: refreshing ? 'none' : 'auto'
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
            <EnhancedPlanogram
              machineData={enrichedSelectedMachine}
              selectedSlots={selectedSlots}
              onSlotInspect={handleSlotClick}
              onSlotHover={experimentMode ? handleSlotHover : undefined}
              onSlotLeave={experimentMode ? handleSlotLeave : undefined}
              hideTooltips={experimentMode}
              slotMarkers={slotMarkers}
              onMachineClick={(machine) => {
                setState(prev => ({ ...prev, selectedMachineId: enrichedSelectedMachine.machineId }));
              }}
              heatmapMode={experimentMode ? 'units' : 'products'}
              compact={true}
              period={period}
            />
            </Box>
            {experimentMode && (
              <ExperimentModePanel
                clientId={currentUser.customer_id}
                machine={enrichedSelectedMachine}
                lanes={queuedLanes}
                pointer={pointer}
                busyExperiment={pointer && busyKey === `${pointer.slot.row}-${pointer.slot.column}` ? pointer.slot.experiment : null}
                picking={picking}
                onStartPicking={() => setPicking(true)}
                onBack={() => { setPicking(false); setIncoming(null); }}
                incoming={incoming}
                previewIncoming={previewIncoming}
                onIncomingChange={setIncoming}
                onRemoveLanes={handleRemoveQueued}
                onRecommendations={setRecommendations}
                onCreated={handleExperimentsCreated}
                suggestion={suggestion}
                onCloseSuggestion={() => setSuggestionKey(null)}
                onSuggestionResolved={handleSuggestionResolved}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Slot Detail Drawer */}
      <SlotDetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerSlotKey(null); }}
        slot={drawerSlotData}
        machineData={enrichedSelectedMachine}
        clientId={currentUser.customer_id}
        onJumpToSlot={handleSlotInspect}
        onBack={alertLink ? handleBackToAlerts : undefined}
        period={period}
      />
    </Box>
  );
};


const Analysis = () => null;

export { POSAnalysisTab };
export default Analysis;
